/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { Button, DialogActions, DialogContent, DialogTitle } from "@octopusdeploy/design-system-components";
import type { ProcessTemplateParameterResource } from "@octopusdeploy/octopus-server-client";
import { ControlType, isAccountControlType, isImportableValueControlType, Permission, ProcessType } from "@octopusdeploy/octopus-server-client";
import * as React from "react";
import { useProducerTemplateProcessContext } from "~/areas/projects/components/Process/ProjectTemplates/useProducerTemplateProcessContext";
import { useLegacyDoBusyTask } from "~/components/DataBaseComponent/useLegacyDoBusyTask";
import { isFeatureToggleEnabled } from "~/components/FeatureToggle/New/FeatureToggleContext";
import type { PagedOnboardingDialogPageData } from "~/components/OnboardingDialog/PagedOnboardingDialog";
import { PagedOnboardingDialog } from "~/components/OnboardingDialog/PagedOnboardingDialog";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import { dropdownOptionsValidator } from "~/components/form/Validators";
import { ParameterConfigurationView } from "./DialogViews/ParameterConfigurationView";
import { ParameterDefaultValueView } from "./DialogViews/ParameterDefaultValueView";
import { ParameterConfigurationHelpContent, ParameterConfigurationHelpImage } from "./DialogViews/components/ProcessTemplateParameterDialogHelp";
import type { TemplateValueKey } from "./TemplateParametersTable";
interface TemplateParameterEditorDialogProps {
    allParameters: ProcessTemplateParameterResource[];
    parameter?: ProcessTemplateParameterResource;
    onOk(parameter: ProcessTemplateParameterResource): void;
    processTemplateLink?: string | undefined;
    emptyParameterDefaults?: Partial<ProcessTemplateParameterResource>;
    disableControlType?: boolean;
    close?: () => void;
    open?: boolean;
}
const PARAMETER_EDIT_PERMISSION = {
    permission: Permission.PlatformHubEdit,
};
const EMPTY_PARAMETER: ProcessTemplateParameterResource = {
    Id: "",
    Links: {},
    Name: "",
    Label: "",
    HelpText: "",
    DisplaySettings: {},
    IsOptional: false,
    Values: [],
};
const CONTROL_TYPE_KEY = "Octopus.ControlType";
const SELECT_OPTIONS_KEY = "Octopus.SelectOptions";
const createEmptyParameter = (defaults?: Partial<ProcessTemplateParameterResource>): ProcessTemplateParameterResource => ({
    ...EMPTY_PARAMETER,
    ...defaults,
});
const TemplateParameterEditorDialog: React.FC<TemplateParameterEditorDialogProps> = (props) => {
    const templateContext = useProducerTemplateProcessContext();
    const { parameter, allParameters, onOk, processTemplateLink, disableControlType, close, open } = props;
    const initialParameter = parameter || createEmptyParameter(props.emptyParameterDefaults);
    const [model, setModel] = React.useState<ProcessTemplateParameterResource>(initialParameter);
    const [originalName, setOriginalName] = React.useState<string>(initialParameter.Name);
    const [validationErrors, setValidationErrors] = React.useState<Record<string, string>>({});
    const [focusedValueKey, setFocusedValueKey] = React.useState<TemplateValueKey | null>(null);
    const [isEditingScope, setIsEditingScope] = React.useState(false);
    const { doBusyTask } = useLegacyDoBusyTask();
    const isEditing = !!parameter;
    const controlType = model.DisplaySettings[CONTROL_TYPE_KEY] ?? ControlType.SingleLineText;
    const shouldShowSensitiveDefaultPage = (controlType: ControlType | undefined): boolean => {
        return isFeatureToggleEnabled("process-template-sensitive-parameters") && templateContext.type === ProcessType.ProcessTemplate && controlType === ControlType.Sensitive;
    };
    const showDefaultValuePage = !isEditing && (isImportableValueControlType(controlType) || shouldShowSensitiveDefaultPage(controlType) || isAccountControlType(controlType));
    const updateModel = (newValues: Partial<ProcessTemplateParameterResource>) => {
        if (Object.keys(validationErrors).length > 0) {
            setValidationErrors({});
        }
        setModel((prevModel) => ({
            ...prevModel,
            ...newValues,
        }));
    };
    const validateParameterConfiguration = async (): Promise<boolean> => {
        const errors: Record<string, string> = {};
        if (!isEditing && !model.DisplaySettings[CONTROL_TYPE_KEY]) {
            errors["ControlType"] = "Please provide a control type for the parameter.";
        }
        if (model.DisplaySettings[CONTROL_TYPE_KEY] === ControlType.Select) {
            const selectOptionsError = dropdownOptionsValidator()(model.DisplaySettings[SELECT_OPTIONS_KEY]!);
            if (selectOptionsError !== "") {
                errors["SelectOptions"] = `Drop down options are not formatted correctly. ${selectOptionsError}.`;
            }
        }
        const trimmedName = model.Name.trim() ?? "";
        if (!trimmedName) {
            errors["Name"] = "Please provide a name for the parameter.";
        }
        else if (trimmedName.startsWith("Octopus.Action.")) {
            errors["Name"] = "Parameter name cannot start with Octopus.Action.";
        }
        else if (allParameters.some((p) => p.Name === trimmedName && p.Name !== originalName)) {
            errors["Name"] = "This parameter name already exists. Parameter names should be unique.";
        }
        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };
    const getFieldError = (fieldName: string): string | undefined => validationErrors[fieldName];
    const modelWithTrimmedName = { ...model, Name: model.Name.trim() ?? "" };
    const onCreate = async () => {
        if (await validateParameterConfiguration()) {
            onOk(modelWithTrimmedName);
            close?.();
        }
    };
    const onUpdate = async () => {
        if (await validateParameterConfiguration()) {
            onOk(model);
            close?.();
        }
    };
    const hasPermission = isAllowed(PARAMETER_EDIT_PERMISSION);
    const getTitle = (pageIndex: number): string => {
        if (isEditing)
            return "Update Parameter";
        if (pageIndex === 0)
            return "Add Parameter";
        if (isAccountControlType(controlType) && model.Values.length > 0)
            return "Add Parameter Values";
        return "Add Parameter Value";
    };
    const excludedControlTypeOptions = templateContext.type === ProcessType.ProjectTemplate ? [ControlType.StepName] : [];
    const pageList: PagedOnboardingDialogPageData[] = [
        {
            name: "Add Parameter",
            title: getTitle(0),
            isPageModelValid: validateParameterConfiguration,
            render: () => (<ParameterConfigurationView model={model} setModel={updateModel} getFieldError={getFieldError} isEditing={isEditing} disableControlType={disableControlType} processTemplateLink={processTemplateLink} originalName={originalName} excludedControlTypeOptions={excludedControlTypeOptions}/>),
            customPrimaryButton: showDefaultValuePage ? undefined : <Button label="Create Parameter" importance="primary" onClick={onCreate}/>,
        },
    ];
    if (showDefaultValuePage) {
        pageList.push({
            name: "Add Parameter",
            title: getTitle(1),
            hideBackButton: true,
            customSecondaryButton: (pageNavigation) => (<Button label="Back" importance="secondary" onClick={() => {
                    updateModel({ Values: [] });
                    pageNavigation.moveToPreviousPage();
                }}/>),
            customPrimaryButton: <Button label="Create Parameter" importance="primary" onClick={onCreate}/>,
            render: () => (<ParameterDefaultValueView model={model} setModel={updateModel} controlType={controlType} focusedValueKey={focusedValueKey} setFocusedValueKey={setFocusedValueKey} isEditingScope={isEditingScope} setIsEditingScope={setIsEditingScope} doBusyTask={doBusyTask}/>),
        });
    }
    if (!hasPermission) {
        return null;
    }
    // For editing, render as dialog content (used with DialogOpener)
    if (isEditing && close && !open) {
        return (<>
                <DialogTitle>Update Parameter</DialogTitle>
                <DialogContent>
                    <ParameterConfigurationView model={model} setModel={updateModel} getFieldError={getFieldError} isEditing={isEditing} disableControlType={disableControlType} processTemplateLink={processTemplateLink} originalName={originalName} excludedControlTypeOptions={excludedControlTypeOptions}/>
                </DialogContent>
                <DialogActions>
                    <Button label="Cancel" importance="secondary" onClick={close}/>
                    <Button label="Update" importance="primary" onClick={onUpdate}/>
                </DialogActions>
            </>);
    }
    // For create with multi-page, render as standalone dialog (PagedOnboardingDialog manages its own dialog)
    return (<PagedOnboardingDialog open={open ?? true} close={(isCancel) => close?.()} name="Process Template Parameter Editor" showPageIndicator={showDefaultValuePage} pages={pageList} hidePreviousButton={false} hideCompleteButton={true} contentPanelHeight={600} helpPanelTheme={"light"} helpPanelContent={ParameterConfigurationHelpContent} helpPanelImage={ParameterConfigurationHelpImage}/>);
};
TemplateParameterEditorDialog.displayName = "TemplateParameterEditorDialog"
TemplateParameterEditorDialog.displayName = "TemplateParameterEditorDialog";
export default TemplateParameterEditorDialog;
